import { useState } from "react";
import { FaEyeSlash } from "react-icons/fa";

import { EyeIcon } from "../assets/icons";
import { useDarkMode } from "../context/DarkModeContext";
import { InputProps, SelectInputProps } from "./types";

export const Input = ({
  type = "text",
  placeholder,
  required = false,
  icon,
  register,
  name,
  className = "",
  isPassword = false,
  value,
  onChange,
  error,
  disabled = false,
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const { darkMode } = useDarkMode();

  const baseInputStyles = `
    w-full p-3 border rounded-lg pl-4 
    bg-white text-black dark:bg-black dark:text-white 
    placeholder-gray-400 dark:placeholder-white 
    focus:outline-none focus:ring-2 focus:ring-blue-500
    ${error ? "border-red-500" : "border-gray-300"}
    ${disabled ? "opacity-50 cursor-not-allowed" : ""}
  `;

  const combinedClassName = `${baseInputStyles} ${className}`;

  return (
    <div className="relative">
      <input
        type={isPassword ? (showPassword ? "text" : "password") : type}
        placeholder={placeholder}
        {...(register && name ? register(name) : {})}
        className={combinedClassName}
        required={required}
        aria-label={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />

      <div className="absolute top-1/2 right-3 transform -translate-y-1/2">
        {isPassword ? (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="focus:outline-none"
            aria-label={showPassword ? "Hide password" : "Show password"}
            disabled={disabled}
          >
            {showPassword ? (
              <EyeIcon theme={darkMode ? "dark" : "light"} />
            ) : (
              <FaEyeSlash className="text-gray-400 dark:text-white" />
            )}
          </button>
        ) : (
          icon && <div className="dark:text-white text-gray-400">{icon}</div>
        )}
      </div>

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export const SelectInput = ({
  options,
  register,
  name,
  placeholder = "Select an option",
  required = false,
  error,
  disabled = false,
}: SelectInputProps) => {
  const selectStyles = `
    w-full p-3 border rounded-lg pl-4 pr-8
    bg-white text-black dark:bg-black dark:text-white 
    focus:outline-none focus:ring-2 focus:ring-blue-500
    ${error ? "border-red-500" : "border-gray-300"}
    ${disabled ? "opacity-50 cursor-not-allowed" : ""}
    appearance-none
  `;

  return (
    <div className="relative">
      <select
        {...register(name)}
        className={selectStyles}
        required={required}
        defaultValue=""
        disabled={disabled}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
        <svg
          className="fill-current h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
        >
          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
        </svg>
      </div>
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};
